import React, { Component } from 'react';
import { achievementsRef } from '../config/firebase';
import { onValue, set, child, update, remove } from 'firebase/database';
import { showToast } from '../actions/toastBus';
import PageLoader from './PageLoader';
import {
    LuAward,
    LuMedal,
    LuGraduationCap,
    LuPencilLine,
    LuSparkles,
    LuPlus,
    LuSave,
    LuBuilding2,
    LuCalendar,
    LuTag,
    LuX,
    LuTrash2
} from 'react-icons/lu';
import '../stylesheet/style.css';
import '../stylesheet/Achievements.css';

const TAGS = ['Award', 'Competition', 'Academic'];

const ICONS_BY_TAG = {
    Award: LuAward,
    Competition: LuMedal,
    Academic: LuGraduationCap
};

export default class Achievements extends Component {

    constructor(props) {
        super(props);
        this.state = {
            achievements: [],
            contentLoading: true,
            isLoggedIn: false,
            newAchievement: { title: '', org: '', date: '', tag: 'Award' },
            // null = the modal is adding a new achievement; otherwise the
            // id (real Firebase index) of the achievement being edited.
            editingId: null,
            modalOpen: false,
            modalClosing: false
        };
    }

    componentDidMount() {
        this.setState({ isLoggedIn: localStorage.getItem('adminLoggedIn') });
        onValue(achievementsRef, (snapshot) => {
            const data = snapshot.val();
            // Firebase only returns a real array when keys are sequential
            // (0,1,2...). Data typed straight into the console usually gets
            // auto-generated keys, which comes back as a plain object and
            // would otherwise crash achievements.map() below.
            const achievements = Array.isArray(data)
                ? data
                : (data ? Object.values(data) : []);
            this.setState({
                achievements,
                contentLoading: false
            });
        });
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._modalTimer);
    }

    handleKeyDown = (event) => {
        if (event.key === 'Escape' && this.state.modalOpen) {
            this.closeModal();
        }
    }

    // Same popup handles both jobs: null editingId = blank form for a new
    // achievement, otherwise the form is pre-filled from that card and
    // saves back to its own Firebase key instead of creating a new one.
    openAddModal = () => {
        this.setState({
            editingId: null,
            newAchievement: { title: '', org: '', date: '', tag: 'Award' },
            modalOpen: true,
            modalClosing: false
        });
    }

    openEditModal = (id, achievement) => {
        this.setState({
            editingId: id,
            newAchievement: {
                title: achievement.title || '',
                org: achievement.org || '',
                date: achievement.date || '',
                tag: achievement.tag || 'Award'
            },
            modalOpen: true,
            modalClosing: false
        });
    }

    closeModal = () => {
        // Match the exit-animation timing (see .achievement-modal-* in
        // Achievements.css) before actually unmounting, same pattern as
        // the Home profile modal.
        this.setState({ modalClosing: true });
        window.clearTimeout(this._modalTimer);
        this._modalTimer = window.setTimeout(() => {
            this.setState({ modalOpen: false, modalClosing: false, editingId: null });
        }, 320);
    }

    handleNewAchievementChange = (field, value) => {
        this.setState((prev) => ({
            newAchievement: { ...prev.newAchievement, [field]: value }
        }));
    }

    saveAchievement = () => {
        const { title, org, date, tag } = this.state.newAchievement;
        if (!title || !org || !date || !tag) return showToast('Fill in every field before saving.', 'error');
        update(achievementsRef, {
            [this.state.editingId]: { title, org, date, tag }
        })
            .then(() => {
                this.closeModal();
                showToast('Achievement updated', 'success');
            })
            .catch((error) => {
                console.error('Error updating achievement:', error);
                showToast('Could not update the achievement - please try again.', 'error');
            });
    }

    deleteAchievement = () => {
        if (this.state.editingId === null) return;
        if (!window.confirm('Delete this achievement? This can\'t be undone.')) return;

        remove(child(achievementsRef, this.state.editingId.toString()))
            .then(() => {
                this.closeModal();
                showToast('Achievement deleted', 'success');
            })
            .catch((error) => {
                console.error('Error deleting achievement:', error);
                showToast('Could not delete the achievement - please try again.', 'error');
            });
    }

    addNewAchievement = () => {
        const { title, org, date, tag } = this.state.newAchievement;
        if (!title || !org || !date || !tag) return showToast('Fill in every field before adding.', 'error');

        const id = this.state.achievements.length.toString(); // Firebase keys must be strings

        set(child(achievementsRef, id), this.state.newAchievement)
            .then(() => {
                this.setState({ newAchievement: { title: '', org: '', date: '', tag: 'Award' } });
                this.closeModal();
                showToast('New achievement added', 'success');
            })
            .catch((error) => {
                console.error('Error saving achievement:', error);
                showToast('Could not add the achievement - please try again.', 'error');
            });
    }

    handleModalSubmit = () => {
        if (this.state.editingId !== null) {
            this.saveAchievement();
        } else {
            this.addNewAchievement();
        }
    }

    render() {
        const { achievements, newAchievement, isLoggedIn, contentLoading, modalOpen, modalClosing, editingId } = this.state;
        // Show the most recently added achievement first, same as
        // Projects/Education, while keeping each entry's real array index
        // (id) so admin edits still save to the right Firebase key.
        const ordered = achievements
            .map((achievement, id) => ({ achievement, id }))
            .filter((entry) => entry.achievement)
            .slice()
            .reverse();

        return (
            <div className="base">
                <div className="main-content-outer">
                    <h1 className="sr-only">Achievements</h1>
                    <span className="page-watermark" aria-hidden="true">Achievements</span>
                    {contentLoading && <PageLoader />}
                    {!contentLoading && (
                    <>
                    <div className="achievements-container">
                        {ordered.map(({ achievement, id }) => {
                            const Icon = ICONS_BY_TAG[achievement.tag] || LuAward;
                            return isLoggedIn ? (
                                <div
                                    className="achievement-card achievement-card-clickable"
                                    key={id}
                                    onClick={() => this.openEditModal(id, achievement)}
                                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openEditModal(id, achievement))}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Edit ${achievement.title}`}
                                >
                                    <span className={`achievement-tag tag-${(achievement.tag || 'award').toLowerCase()}`}>{achievement.tag || 'Award'}</span>
                                    <span className="achievement-icon"><Icon/></span>
                                    <h3>{achievement.title}</h3>
                                    <p>{achievement.org}</p>
                                    <span className="achievement-date">{achievement.date}</span>
                                    <span className="achievement-edit-hint"><LuPencilLine /> Edit</span>
                                </div>
                            ) : (
                                <div className="achievement-card" key={id}>
                                    <span className={`achievement-tag tag-${(achievement.tag || 'award').toLowerCase()}`}>{achievement.tag || 'Award'}</span>
                                    <span className="achievement-icon"><Icon/></span>
                                    <h3>{achievement.title}</h3>
                                    <p>{achievement.org}</p>
                                    <span className="achievement-date">{achievement.date}</span>
                                </div>
                            );
                        })}
                    </div>

                    {isLoggedIn && (
                        <button
                            type="button"
                            className="achievement-fab"
                            onClick={this.openAddModal}
                            aria-label="Add new achievement"
                        >
                            <LuPlus />
                        </button>
                    )}

                    {modalOpen && (
                        <div
                            className={`achievement-modal-backdrop${modalClosing ? ' is-closing' : ''}`}
                            onClick={this.closeModal}
                        >
                            <div className="achievement-modal-panel" onClick={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className="achievement-modal-close"
                                    onClick={this.closeModal}
                                    aria-label="Close"
                                >
                                    <LuX />
                                </button>
                                <span className="achievement-add-eyebrow">
                                    {editingId !== null ? <><LuPencilLine /> Editing</> : <><LuSparkles /> Admin</>}
                                </span>
                                <h3 className="achievement-add-heading">
                                    {editingId !== null ? 'Edit achievement' : 'Add a new achievement'}
                                </h3>
                                <label className="achievement-field">
                                    <span className="achievement-field-label">Title</span>
                                    <input
                                        type="text"
                                        placeholder="e.g. 1st Place, Hackathon XYZ"
                                        value={newAchievement.title}
                                        onChange={(e) => this.handleNewAchievementChange('title', e.target.value)}
                                        className="achievement-field-input"
                                        autoFocus
                                    />
                                </label>
                                <label className="achievement-field">
                                    <span className="achievement-field-label"><LuBuilding2 /> Organization</span>
                                    <input
                                        type="text"
                                        placeholder="e.g. IEEE"
                                        value={newAchievement.org}
                                        onChange={(e) => this.handleNewAchievementChange('org', e.target.value)}
                                        className="achievement-field-input"
                                    />
                                </label>
                                <div className="achievement-field-row">
                                    <label className="achievement-field">
                                        <span className="achievement-field-label"><LuCalendar /> Date</span>
                                        <input
                                            type="text"
                                            placeholder="e.g. Mar 2024"
                                            value={newAchievement.date}
                                            onChange={(e) => this.handleNewAchievementChange('date', e.target.value)}
                                            className="achievement-field-input"
                                        />
                                    </label>
                                    <label className="achievement-field">
                                        <span className="achievement-field-label"><LuTag /> Category</span>
                                        <select
                                            value={newAchievement.tag}
                                            onChange={(e) => this.handleNewAchievementChange('tag', e.target.value)}
                                            className="achievement-field-input achievement-field-select"
                                        >
                                            {TAGS.map((tag) => <option value={tag} key={tag}>{tag}</option>)}
                                        </select>
                                    </label>
                                </div>
                                <div className="achievement-modal-actions">
                                    <button
                                        onClick={this.handleModalSubmit}
                                        className="app-form-button achievement-save-cta"
                                    >
                                        {editingId !== null ? <><LuSave /> Save changes</> : <><LuPlus /> Add achievement</>}
                                    </button>
                                    {editingId !== null && (
                                        <button
                                            type="button"
                                            onClick={this.deleteAchievement}
                                            className="achievement-delete-cta"
                                        >
                                            <LuTrash2 /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        );
    }
}
